import React, { useCallback, useEffect, useState } from 'react';
import './InvoiceArchive.css';
import { API_BASE_URL, authFetch } from '../../config/api.config';
import { statusKlasse, dateinameAusHeader } from '../../utils/rechnungsarchiv';
import {
  GutscheinBeleg, GUTSCHEINRECHNUNG_HINWEIS, filterGutscheinBelege, summenGutscheinBelege,
} from '../../utils/gutscheinRechnungen';

/**
 * T-199 (v1.8.70, Session 47, Kundin 13.10.2026 07:52 "warum ich die RG zu dem Gutschein nicht sehen konnte? ... auf den auch die
 * Buchhaltung Zugriff haben muss"): Gutscheinrechnungen (GS-JJJJ-NNNN) in der Rechnungsverwaltung, nur lesend mit PDF.
 * Quelle: Rechnungsarchiv api/invoice-archive?quelle=Gutschein (Policy AccountingAccess, also auch für die Buchhaltung), ohne Zeitraum.
 * Bezahlt wird über "Zahlung bestätigen" (Gutscheine) bzw. den Bankabgleich, nicht hier.
 */
const formatCurrency = (n: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n || 0);
const formatDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-');

const GutscheinRechnungen: React.FC<{ suchbegriff?: string; onAnzahl?: (anzahl: number) => void }> = ({ suchbegriff, onAnzahl }) => {
  const [belege, setBelege] = useState<GutscheinBeleg[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [suche, setSuche] = useState(suchbegriff || '');
  const [status, setStatus] = useState('');
  const [pdfId, setPdfId] = useState<number | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const response = await authFetch(`${API_BASE_URL}/invoice-archive?quelle=Gutschein`);
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.message || `HTTP ${response.status}`);
      const liste: GutscheinBeleg[] = data?.belege || [];
      setBelege(liste);
      if (onAnzahl) onAnzahl(liste.length);
    } catch (err: any) {
      setFehler(`Gutscheinrechnungen konnten nicht geladen werden: ${err.message}`);
    } finally {
      setLaedt(false);
    }
  }, [onAnzahl]);

  useEffect(() => { laden(); }, [laden]);
  useEffect(() => { if (suchbegriff !== undefined) setSuche(suchbegriff); }, [suchbegriff]);

  const pdf = async (b: GutscheinBeleg) => {
    setPdfId(b.id);
    setFehler(null);
    try {
      const response = await authFetch(`${API_BASE_URL}/invoice-archive/Gutschein/${b.id}/pdf`);
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = dateinameAusHeader(response.headers.get('Content-Disposition'), `${b.nummer}.pdf`).replace(/^Gutschein-\d+\.pdf$/, `${b.nummer}.pdf`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setFehler(`PDF konnte nicht geladen werden: ${err.message}`);
    } finally {
      setPdfId(null);
    }
  };

  const gefiltert = filterGutscheinBelege(belege || [], suche, status);
  const summen = summenGutscheinBelege(gefiltert);

  return (
    <div className="invoices-list">
      <p className="section-hint" style={{ margin: '0 0 0.75rem 0' }}>{GUTSCHEINRECHNUNG_HINWEIS}</p>
      {fehler && <div className="error-message">{fehler}</div>}
      <div className="search-filter-bar">
        <input
          type="text"
          className="search-input"
          placeholder="Suche nach Rechnungsnr. (GS-...) oder Käufer..."
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
        />
        <select className="status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Alle Status</option>
          <option value="Offen">Offen</option>
          <option value="Bezahlt">Bezahlt</option>
          <option value="Storniert">Storniert</option>
        </select>
        <button className="btn-secondary" onClick={laden} disabled={laedt}>{laedt ? 'Lädt ...' : 'Aktualisieren'}</button>
        <div className="results-count">
          {gefiltert.length} von {(belege || []).length} Gutscheinrechnungen
        </div>
      </div>

      {laedt && !belege ? (
        <div className="loading">Lädt Gutscheinrechnungen...</div>
      ) : (
        <table className="invoices-table">
          <thead>
            <tr>
              <th className="col-number">Rechnungsnr.</th>
              <th className="col-date">Datum</th>
              <th className="col-customer">Käufer</th>
              <th className="col-booking">Gutschein</th>
              <th className="col-amount">Betrag</th>
              <th className="col-amount">Offen</th>
              <th className="col-status">Status</th>
              <th>Zahlungsart</th>
              <th className="col-actions">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {gefiltert.map((b) => (
              <tr key={b.id} className={b.status === 'Storniert' ? 'archiv-row-storniert' : ''}>
                <td className="invoice-number">{b.nummer}</td>
                <td className="date">{formatDate(b.datum)}</td>
                <td className="customer-name">{b.empfaenger || '-'}</td>
                <td className="booking-number">{b.bezug || '-'}</td>
                <td className="amount">{formatCurrency(b.brutto)}</td>
                <td className={`amount ${b.offen > 0 ? 'amount-open' : 'amount-paid'}`}>{formatCurrency(b.offen)}</td>
                <td>
                  <span className={`archiv-status ${statusKlasse(b.status)}`} title={b.status}></span> {b.status}
                  {b.bemerkung && <div style={{ fontSize: '0.75em', color: '#6b7280' }}>{b.bemerkung}</div>}
                </td>
                <td>{b.zahlungsart || '-'}</td>
                <td className="actions">
                  <button className="btn-action btn-pdf" onClick={() => pdf(b)} disabled={pdfId !== null} title="PDF herunterladen">
                    {pdfId === b.id ? '⏳' : '📄'} PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {belege && gefiltert.length > 0 && (
        <div className="results-count" style={{ marginTop: '0.5rem' }}>
          Summe ohne Stornierte: {formatCurrency(summen.brutto)}, bezahlt {formatCurrency(summen.bezahlt)}, offen {formatCurrency(summen.offen)}
        </div>
      )}
      {belege && belege.length === 0 && (
        <div className="empty-state"><p>Keine Gutscheinrechnungen vorhanden</p></div>
      )}
    </div>
  );
};

export default GutscheinRechnungen;
